// Auth helpers backed by the Phase 1B FastAPI session endpoints.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError } from "@/lib/api";
import type { User } from "@/lib/types";

const ME_KEY = ["/auth/me"] as const;

/**
 * Returns the currently signed-in user, or `null` if 401.
 * Other errors propagate so the UI can surface them.
 */
export function useCurrentUser() {
  return useQuery<User | null, ApiError>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const res = await apiFetch("/auth/me");
        return (await res.json()) as User;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    // Auth state should be reasonably fresh.
    staleTime: 60_000,
    retry: (count, err) => {
      // Don't retry auth failures.
      if (err instanceof ApiError && err.status === 401) return false;
      return count < 1;
    },
  });
}

export function loginWithGoogle(): void {
  window.location.href = "/auth/google/login";
}

export async function logout(): Promise<void> {
  try {
    await apiFetch("/auth/logout", { method: "POST" });
  } catch {
    // Even if the server hasn't shipped logout yet, blow the cookie locally.
  }
}

interface UseLogoutOptions {
  onAfter?: () => void;
}

/**
 * Logs the user out and invalidates the cached `/auth/me` query. Pass
 * `onAfter` to navigate (or run any other side effect) once the logout
 * completes — useful for the UserMenu, which routes back to /login.
 */
export function useLogout() {
  const qc = useQueryClient();
  return async (opts?: UseLogoutOptions) => {
    await logout();
    await qc.invalidateQueries({ queryKey: ME_KEY });
    opts?.onAfter?.();
  };
}
